import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const apiIntegrationSection: DocsSection = {
  label: "API Integration",
  pages: [
    {
      slug: "api-overview",
      title: "Overview",
      description: "Who talks to what — public ALB vs internal Service Connect.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "There are two ways the products are reached. Operators and integration tests hit the ",
            { strong: "public ALB" },
            " (the ",
            { code: "*.yango.moveodevelop.com" },
            " hosts, IP-allowlisted). Once deployed, ",
            { code: "apps/api" },
            " reaches the products over ",
            { strong: "ECS Service Connect" },
            " on the private network — never the public ALB.",
          ],
        },
        {
          kind: "table",
          headers: ["Product", "Auth", "Public host"],
          rows: [
            [{ strong: "ERPNext" }, "Header token", { code: "erp-<env>.yango.moveodevelop.com" }],
            [
              { strong: "UnoPIM" },
              "OAuth2 password grant",
              { code: "pim-<env>.yango.moveodevelop.com" },
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Clients never integrate directly",
          text: "Web/mobile apps never call ERPNext or UnoPIM. They call apps/api, which holds the product secrets server-side and translates to domain DTOs.",
        },
      ],
    },
    {
      slug: "erpnext-api",
      title: "ERPNext (ERP)",
      description: "Header token auth, endpoints, curl examples.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "ERPNext uses header-token auth (",
            { code: "api_key:api_secret" },
            "). There are TWO tokens by privilege: the ADMIN token ",
            { code: "yango/<env>/erpnext-admin-api" },
            " (the Administrator / System-Manager user — used by ops & the provisioning scripts, and for direct curl below), and the SCOPED runtime token ",
            { code: "yango/<env>/erpnext-buyer-api" },
            " (the least-privilege ",
            { code: "svc-buyer-api" },
            " user that apps/api actually authenticates as).",
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "Ready on staging only",
          text: "The ERPNext REST API is ready on staging. On dev the erpnext-admin-api secret is still a placeholder (no keys generated) — the examples below will not authenticate on dev yet.",
        },
        {
          kind: "heading",
          id: "erp-curl",
          text: "Fetch items (curl)",
        },
        {
          kind: "code",
          language: "bash",
          code: 'ERP_HOST="https://erp-staging.yango.moveodevelop.com"\nAPI_KEY="..."      # from yango/staging/erpnext-admin-api\nAPI_SECRET="..."\n\ncurl -s "$ERP_HOST/api/resource/Item?limit_page_length=5" \\\n  -H "Authorization: token $API_KEY:$API_SECRET"',
          caption: "List the first 5 Items. Must originate from an allowlisted IP.",
        },
        {
          kind: "heading",
          id: "erp-endpoints",
          text: "Endpoint shapes",
        },
        {
          kind: "table",
          headers: ["Pattern", "Use"],
          rows: [
            [
              { code: "/api/resource/<DocType>" },
              "CRUD over a DocType (e.g. Item, Customer, Sales Order).",
            ],
            [{ code: "/api/method/<dotted.path>" }, "Call a whitelisted server method."],
          ],
        },
      ],
    },
    {
      slug: "ezcount-api",
      title: "EZCount (accounting)",
      description: "Document issuance — auth models, endpoints, doc-type codes.",
      stub: false,
      blocks: [
        {
          kind: "callout",
          tone: "warn",
          title: "Planned — not yet built in apps/api",
          text: [
            "Discovery is complete — the technical model (2026-07-08 vendor call, ",
            { code: "agent-os/meetings/2026-07-08-easycount-integration" },
            ") and the invoicing model (2026-07-19 call, ",
            { code: "agent-os/meetings/2026-07-19-easycount-invoicing-model" },
            "). The narrow per-delivery issuance connector is shaped as ",
            { code: "BF-036" },
            " (ERPNext-side, in yango-customizations); per-business onboarding/OAuth + the monthly billing/consolidation model are ",
            { code: "BF-108" },
            ". Nothing is built yet. API-level facts below come from EZCount's own API (its Postman collections); the model facts come from the two vendor calls — all describe the target design, not a shipped integration.",
          ],
        },
        {
          kind: "paragraph",
          text: [
            "EZCount (vendor spelling ",
            { strong: "EasyCount" },
            ") is the Israeli accounting SaaS that issues the legal document for each order — tax-invoice/receipt, tax invoice, receipt — and reports to the tax authority (",
            { code: "מספר הקצה" },
            ") behind the scenes. It is a document-issuance surface, not a system of record: the app owns the order, EZCount owns the document.",
          ],
        },
        {
          kind: "key-value",
          pairs: [
            { key: "Demo / test", value: { code: "https://demo.ezcount.co.il" } },
            { key: "Production", value: { code: "https://api.ezcount.co.il" } },
            {
              key: "API key",
              value: "Differs per domain — a demo key will not authenticate in production.",
            },
            {
              key: "Transport",
              value: "REST (form-POST or JSON). SSL only; set client timeout > 30s.",
            },
          ],
        },
        {
          kind: "heading",
          id: "ezcount-models",
          text: "Two-phase invoicing model",
        },
        {
          kind: "paragraph",
          text: [
            "The taxable event is Yango's ",
            { strong: "margin" },
            ": Yango buys a good and resells it to the retailer (",
            { code: "מכולת" },
            ") at a markup, so it issues a ",
            { code: "חשבונית מס" },
            " on that margin — the retailer's onward sale to the consumer is the retailer's own bookkeeping, outside Yango's system. The 2026-07-19 call resolved the earlier open question (who is the seller of record?) into a phased rollout:",
          ],
        },
        {
          kind: "table",
          headers: ["Phase", "Seller of record", "Mechanism & status"],
          rows: [
            [
              [{ strong: "1 · Lean" }, " (MVP)"],
              ["Yango — retailer listed as ", { code: "נמען" }, " (recipient)"],
              "All documents from Yango Deli's central EZCount account; margin חשבונית מס. Fastest path (account already exists); non-blocking — API work starts now.",
            ],
            [
              [{ strong: "2 · Distributor" }, " (deferred)"],
              "Each retailer (own identity / logo)",
              [
                "Distributor system (",
                { code: "מערכת מפיצים" },
                ") for D2C warehouse→consumer shipments; a per-retailer Access Token on the ",
                { strong: "same" },
                " createDoc endpoint. Deferred, explicitly non-blocking.",
              ],
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Monthly consolidation goes through the API",
          text: [
            "There is no automatic monthly-consolidation mechanism, and the manual UI has no “select-all” for delivery notes — so to bill a retailer monthly, the integration aggregates the month's ",
            { code: "תעודות משלוח" },
            " (delivery-note) IDs and sends ",
            { strong: "one" },
            " createDoc for the consolidated חשבונית מס (2026-07-19 call). Per-delivery immediate issuance is ",
            { code: "BF-036" },
            "; the per-client billing model + monthly consolidation is ",
            { code: "BF-108" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "ezcount-auth",
          text: "Authentication — three credential systems",
        },
        {
          kind: "table",
          headers: ["Credential", "Obtained via", "Used for"],
          rows: [
            [
              [
                { strong: "Distributor key" },
                " (",
                { code: "api_key" },
                " + ",
                { code: "developer_email" },
                ")",
              ],
              "One-time registration",
              "Manage businesses; issue as Yango",
            ],
            [
              { code: "company_api_key" },
              { code: "POST /api/user/create" },
              "Issue for a business we created",
            ],
            [
              [
                { strong: "OAuth token" },
                " (",
                { code: "access_token" },
                " + ",
                { code: "refresh_token" },
                ")",
              ],
              "OAuth2 consent (existing business)",
              "Issue on a consenting business's behalf",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "EZCount is not a credential store",
          text: [
            "Each credential is returned ",
            { strong: "once" },
            " (the create response, or the OAuth exchange) and cannot be looked up later — ",
            { code: "/v2/api/distributors/users" },
            " lists only ",
            { code: "u_uuid" },
            " + status flags, never keys. Persist each credential in our own encrypted token store, keyed by ",
            { code: "u_uuid" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "ezcount-curl",
          text: "Issue a document (curl)",
        },
        {
          kind: "code",
          language: "bash",
          code: 'curl -s -X POST "https://demo.ezcount.co.il/api/createDoc" \\\n  -H "Content-Type: application/json" \\\n  -d \'{\n    "api_key": "<company_api_key or distributor key>",\n    "developer_email": "you@example.com",\n    "type": 320,\n    "customer_name": "Business Ltd",\n    "customer_crn": "999999999",\n    "transaction_id": "<our-order-id>",\n    "item": [{ "details": "Order line", "price": 99.9, "amount": 1, "vat_type": "INC" }],\n    "price_total": 99.9,\n    "payment": [{ "payment_type": 3, "payment_sum": 99.9 }]\n  }\'',
          caption:
            "This is a receipt type (320), so it carries both price_total and payment[] (a 320 without price_total returns errNum 215); an invoice-only 305 omits both. Returns doc_uuid, doc_number, pdf_link. Pass our order id as transaction_id for idempotency; PDFs are async (pdf_link may 429 for a few minutes).",
        },
        {
          kind: "heading",
          id: "ezcount-doc-types",
          text: "Document type codes",
        },
        {
          kind: "table",
          headers: ["type", "Document"],
          rows: [
            [{ code: "200" }, "תעודת משלוח — Delivery note (not a VAT document)"],
            [{ code: "210" }, "תעודת החזרה — Return note"],
            [{ code: "300" }, "חשבונית עסקה — Proforma"],
            [{ code: "305" }, "חשבונית מס — Tax invoice"],
            [{ code: "320" }, "חשבונית מס קבלה — Invoice-receipt (combined)"],
            [{ code: "330" }, "חשבונית זיכוי — Credit invoice"],
            [{ code: "400" }, "קבלה — Receipt"],
            [{ code: "405" }, "קבלה על תרומה — Donation receipt"],
          ],
        },
        {
          kind: "paragraph",
          text: [
            "Payment types: ",
            { code: "1" },
            " cash, ",
            { code: "2" },
            " check, ",
            { code: "3" },
            " credit card, ",
            { code: "4" },
            " bank transfer, ",
            { code: "8" },
            " direct debit, ",
            { code: "9" },
            " other, ",
            { code: "91" },
            " web transfer (Pay/PayPal).",
          ],
        },
        {
          kind: "heading",
          id: "ezcount-doc-by-phase",
          text: "Which document to issue, by order phase",
        },
        {
          kind: "paragraph",
          text: [
            "The document type depends on the client's ",
            { strong: "billing model" },
            " (chosen at onboarding) and the order's phase. The per-order (immediate) path is ",
            { code: "BF-036" },
            " / this connector (",
            { code: "BF-256" },
            "); the monthly consolidated path is ",
            { code: "BF-108" },
            "; credit notes are ",
            { code: "BF-109" },
            ".",
          ],
        },
        {
          kind: "table",
          headers: ["Billing model", "Order phase", "Document"],
          rows: [
            [
              [{ strong: "Pay per order" }, " (immediate)"],
              "On delivery — credit terms (default)",
              [{ code: "305" }, " חשבונית מס — tax invoice, invoice-only"],
            ],
            [
              [{ strong: "Pay per order" }, " (immediate)"],
              "On payment received",
              [{ code: "400" }, " קבלה — receipt, closes the 305"],
            ],
            [
              [{ strong: "Pay per order" }, " (immediate)"],
              "On delivery — if paid at delivery",
              [{ code: "320" }, " חשבונית מס קבלה — combined invoice + receipt"],
            ],
            [
              [{ strong: "Pay monthly" }, " (consolidated)"],
              "On each delivery",
              [{ code: "200" }, " תעודת משלוח — delivery note, no VAT document yet"],
            ],
            [
              [{ strong: "Pay monthly" }, " (consolidated)"],
              "Month-end consolidation (API-driven)",
              [{ code: "305" }, " חשבונית מס — one invoice consolidating the month's 200s"],
            ],
            [
              [{ strong: "Pay monthly" }, " (consolidated)"],
              "On payment received",
              [{ code: "400" }, " קבלה — receipt, closes the monthly 305"],
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Default is 305; receipt types carry a payment array",
          text: [
            "The default issued type is ",
            { code: "305" },
            " (invoice-only) — the credit-terms-correct default, configurable via ",
            { code: "EZCount Settings.default_doc_type" },
            ". Issuing ",
            { code: "320" },
            " (combined invoice + receipt) instead assumes payment at delivery and is pending Finance sign-off. Receipt types (",
            { code: "320" },
            "/",
            { code: "400" },
            "/",
            { code: "405" },
            ") must carry both ",
            { code: "price_total" },
            " and a ",
            { code: "payment[]" },
            " array; invoice-only ",
            { code: "305" },
            " carries neither.",
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: "Returns, and the 200-vs-300 gotcha",
          text: [
            "A refund is issued as a ",
            { code: "330" },
            " חשבונית זיכוי — the fiscal credit that reverses the VAT (owned by ",
            { code: "BF-109" },
            "), never a re-issue. A physical goods return is a separate, non-fiscal document: ",
            { code: "210" },
            " תעודת החזרה (return note — the reverse of the ",
            { code: "200" },
            " delivery note, no VAT); the money and VAT are what the ",
            { code: "330" },
            " reverses. Note that ",
            { code: "200" },
            " (not ",
            { code: "300" },
            ") is the delivery note — ",
            { code: "300" },
            " is a Proforma.",
          ],
        },
        {
          kind: "heading",
          id: "ezcount-open",
          text: "Open questions (with the vendor)",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              "Tax-Authority allocation (",
              { code: "מספר הקצה" },
              ") timing for invoices over ~₪5,000 (status ",
              { code: "417" },
              " = waiting).",
            ],
            "How the monthly API-vs-UI usage report attributes documents per client.",
            [
              "Where credit management lives — obligo limits (",
              { code: "אובליגו" },
              "), aging (",
              { code: "גיול חובות" },
              "), collections (",
              { code: "גבייה" },
              "). Not in the EZCount UI; home undecided (EZCount API / ERPNext / Priority) and flagged not day-one.",
            ],
            "Phase-2 distributor-system scope & timing (business-logic call, deferred).",
          ],
        },
      ],
    },
    {
      slug: "hyp-api",
      title: "HYP (payments / clearing)",
      description: "Card payments + clearing — surfaces, flow, invoice timing.",
      stub: false,
      blocks: [
        {
          kind: "callout",
          tone: "warn",
          title: "Planned — assumption-check against HYP's docs",
          text: [
            "The payments connector is not built yet: board ",
            { code: "BF-111" },
            " (block-2g) is ",
            { strong: "not started and not yet shaped" },
            ", framed there as an ERPNext↔payment-gateway connector (HYP/Yaad-class), not a decided ",
            { code: "apps/api" },
            " adapter. Product seeded in ",
            { code: "0054_seed_product_hyp.sql" },
            ". Fields below are verbatim from HYP's reference docs (HYP publishes no public developer portal — API access is sales-gated); confirm the surface and exact spellings against HYP's written guide before building.",
          ],
        },
        {
          kind: "paragraph",
          text: [
            "HYP (",
            { code: "הייפ" },
            ") handles card payments and clearing (",
            { code: "סליקה" },
            "; card processing is its Credit Guard line). The model is an ",
            { strong: "iframe + card-verification token + capture-after-delivery" },
            " flow (BF-111's acceptance: the buyer pays online, capture happens after delivery, settlement status is visible to support): the server side holds the credentials and makes every server-side call; the buyer app only loads the iframe. Division of labour — ",
            { strong: "HYP does the charging, EZCount issues the legal invoice" },
            " (BF-037); and HYP owns EZCount, so the invoice call below is EZCount's, not a separate vendor.",
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "Two surfaces — different request shapes",
          text: [
            { code: "pay" },
            ", ",
            { code: "getToken" },
            ", ",
            { code: "soft" },
            ", ",
            { code: "APISign" },
            ", ",
            { code: "commitTrans" },
            ", ",
            { code: "zikoyAPI" },
            " are the ",
            { strong: "Hyp Pay" },
            " query-string API; ",
            { code: "addCgInvoice" },
            " and ",
            { code: "inquireTransactions" },
            " are the ",
            { strong: "Enterprise" },
            " XML API (some fields rename). Which surface we standardise on is still open.",
          ],
        },
        {
          kind: "heading",
          id: "hyp-flow",
          text: "The end-to-end flow",
        },
        {
          kind: "table",
          headers: ["Phase", "Trigger", "Calls"],
          rows: [
            [
              { strong: "A · Order placement" },
              "Buyer reaches checkout",
              [
                { code: "APISign SIGN" },
                " → iframe ",
                { code: "pay" },
                " → ",
                { code: "APISign VERIFY" },
                " → ",
                { code: "getToken" },
              ],
            ],
            [
              { strong: "B · Delivery completes" },
              "Fulfillment fires DELIVERED",
              [{ code: "soft" }, " (charge token / J4 capture) + EZCount invoice"],
            ],
            [
              { strong: "C · Month-end" },
              "Scheduled job",
              [
                "EZCount ",
                { code: "createDoc" },
                " — consolidated invoice (aggregate the month's delivery notes)",
              ],
            ],
            [
              { strong: "D · Refund" },
              "Support action",
              [
                { code: "zikoyAPI" },
                " (settled) / ",
                { code: "CancelTrans" },
                " (unsettled) + credit note",
              ],
            ],
            [{ strong: "E · Reconciliation" }, "Daily cron", { code: "inquireTransactions" }],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Verify the result server-side",
          text: [
            "Never trust the iframe redirect — re-run ",
            { code: "APISign VERIFY" },
            " server-side (",
            { code: "CCode=0" },
            " = authentic). Card tokens come from ",
            { code: "getToken" },
            ", not the redirect, and work even from a verify-only (J5) transaction with ",
            { code: "allowFalse=True" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "hyp-invoicing",
          text: "Invoice timing & document types",
        },
        {
          kind: "paragraph",
          text: [
            "Issue the legal document at ",
            { strong: "capture" },
            " (payment received, ",
            { code: "CCode=0" },
            ") — ",
            { strong: "not" },
            " at settlement (",
            { code: "financialStatus=TRANSMITTED" },
            ", the bank-clearing batch, which belongs to reconciliation). Tax principle: the tax invoice attaches to the sale, the receipt to the payment.",
          ],
        },
        {
          kind: "table",
          headers: ["Document", "What it is", "Allocation #?"],
          rows: [
            [{ code: "חשבונית מס" }, "Tax invoice (VAT doc for the sale)", "Yes ≥ ₪5k"],
            [{ code: "קבלה" }, "Receipt (confirms payment)", "No"],
            [
              { code: "חשבונית מס קבלה" },
              "Combined invoice + receipt (sale & payment together)",
              "Yes ≥ ₪5k",
            ],
            [{ code: "תעודת משלוח" }, "Delivery note (not fiscal; travels with goods)", "No"],
            [{ code: "חשבונית זיכוי" }, "Credit note (refunds)", "No"],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "תעודת משלוח (delivery note) — type code 200",
          text: "In our engagement material it is a first-class EZCount document — the unit the month-end consolidation aggregates into one חשבונית מס (2026-07-19 call), and a core EZCount document type per the product research. It is not in HYP's invoiceType list (HYP charges; it does not issue this); its EZCount type code is 200, confirmed against EZCount documentation (2026-07-21). Legally mandatory per delivery.",
        },
      ],
    },
    {
      slug: "courimax-api",
      title: "Courimax (courier / dispatch)",
      description:
        "Delivery-window reads, outbound order push, and the inbound status webhook — behind CourierPort/DispatchPort.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Courimax is the delivery courier, reached only through ",
            { code: "apps/api" },
            " behind ",
            { code: "CourierPort" },
            " (availability reads) and ",
            { code: "DispatchPort" },
            " (order push + status). The provider is bound by config; the buyer app and the products never learn the courier exists. The lane spans three flows: ",
            { strong: "reads" },
            " (which delivery windows a buyer can pick), an ",
            { strong: "outbound push" },
            " (send a scheduled order to the courier), and an ",
            { strong: "inbound webhook" },
            " (the courier reports status/driver/proof-of-delivery back).",
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: "Off by default — opt-in per environment",
          text: [
            { code: "COURIER_PROVIDER" },
            " defaults to ",
            { code: "none" },
            " (the noop adapter: an empty picker, and the inbound receiver refuses every event). The lane only goes live where an operator sets ",
            { code: "COURIER_PROVIDER=courimax" },
            " and supplies the credential. Until then no order reaches Courimax and no event is accepted.",
          ],
        },
        {
          kind: "heading",
          id: "courimax-config",
          text: "Configuration",
        },
        {
          kind: "key-value",
          pairs: [
            {
              key: "COURIER_PROVIDER",
              value: [{ code: "courimax" }, " | ", { code: "none" }, " (default — noop)"],
            },
            {
              key: "COURIMAX_BASE_URL",
              value: [
                "https base (default ",
                { code: "https://api.courimax.com" },
                "). The host must be on the in-code allow-list.",
              ],
            },
            {
              key: "COURIMAX_API_KEY",
              value: [
                "the ",
                { code: "cmx_…" },
                " key. Sent on every outbound call AND the HMAC key the inbound webhook is verified against — there is no separate webhook secret.",
              ],
            },
            {
              key: "slot horizon (days)",
              value: [
                "how many days of windows to ask for (1–7). ",
                { strong: "No longer an env var" },
                " — since BF-917 it lives in the ERPNext ",
                { code: "Dispatch Settings" },
                " Single (Desk-editable, read at request time), and a courier outage now serves no windows unconditionally (the old ",
                { code: "COURIER_UNAVAILABLE_BEHAVIOR" },
                " / ",
                { code: "erp-fallback" },
                " knob was removed; the order gate stays fail-closed either way).",
              ],
            },
            {
              key: "COURIMAX_PICKUP_LAT / _LNG / _ADDRESS",
              value: [
                "the fulfilment origin the courier collects from. ",
                { strong: "All three or none" },
                " — a partial set is treated as ",
                { code: "dispatch-not-configured" },
                " and the order is not pushed.",
              ],
            },
            {
              key: "COURIMAX_PACKAGE_SIZE",
              value: [
                "the declared consignment size (default ",
                { code: "small" },
                "). Config, not derived — nothing in the platform computes a size for a pallet-scale B2B order.",
              ],
            },
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Pickup origin is moving to the fulfilling store (BF-728)",
          text: [
            "The ",
            { code: "COURIMAX_PICKUP_*" },
            " coordinate config above is the current shape. BF-728 replaces it with a per-order pickup address resolved from the fulfilling store, sent address-only — track that item before relying on the global coordinate.",
          ],
        },
        {
          kind: "heading",
          id: "courimax-outbound",
          text: "Outbound: reads + order push",
        },
        {
          kind: "paragraph",
          text: [
            "A buyer read resolves the delivery address to a courier zone, then asks for every window in the horizon in one call. Placing an order re-matches the chosen window against a live read (nothing the client sent is trusted) and, on the dated branch, pushes the order to the courier ",
            { strong: "after" },
            " the Sales Order is written — fail-soft: a courier failure never 5xxes a written order, it is recorded as a dispatch failure instead.",
          ],
        },
        {
          kind: "table",
          headers: ["Purpose", "Courier endpoint"],
          rows: [
            [{ strong: "Resolve zone" }, { code: "POST /api/promises/resolve-zone" }],
            [
              { strong: "Availability (bulk)" },
              { code: "GET /api/public/delivery-slots/availability/bulk" },
            ],
            [{ strong: "Create order" }, { code: "POST /api/integrations/erpnext/orders" }],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "There is a live contract probe",
          text: [
            "Because the upstream is only intermittently available, ",
            { code: "pnpm --filter api probe:courimax" },
            " drives the REAL shipped client/adapter against the configured base URL (route matrix; ",
            { code: "-- --erpnext-lane" },
            " checks the order-create key binding). The API key is never printed.",
          ],
        },
        {
          kind: "heading",
          id: "courimax-outbound-security",
          text: "Outbound security",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { strong: "HTTPS-only + an EXACT host allow-list" },
              " (a module code constant, never config-derived): ",
              { code: "api.courimax.com" },
              " and ",
              { code: "api.dev.courimax.com" },
              ". A mis-set or hostile base URL is refused at client construction — matching is exact-equality, so ",
              { code: "api.courimax.com.attacker.example" },
              " is rejected.",
            ],
            [
              { strong: "DNS-rebinding defence" },
              " — resolve once, validate the address is public, connect to the pinned IP. Any private/loopback/CGNAT address is refused.",
            ],
            [
              "The API key rides on every request but is ",
              { strong: "never logged" },
              "; the delivery address is bounded, control-char-rejected and sanitised before it goes on the wire.",
            ],
          ],
        },
        {
          kind: "heading",
          id: "courimax-webhook",
          text: "Inbound: the order-status webhook",
        },
        {
          kind: "paragraph",
          text: [
            "Courimax POSTs status/driver/proof-of-delivery events to ",
            { code: "POST /api/dispatch/events" },
            ". The route carries no vendor name (a second courier would post to the same path). It is publicly reachable by necessity but ",
            { strong: "not" },
            " unauthenticated — every request must carry a valid HMAC signature over the raw body.",
          ],
        },
        {
          kind: "code",
          language: "bash",
          code: `# Courimax signs the RAW body with COURIMAX_API_KEY (HMAC-SHA256) and sends:
curl -X POST https://<api-host>/api/dispatch/events \\
  -H "Content-Type: application/json" \\
  -H "X-Courimax-Signature: sha256=<hex-hmac-of-raw-body>" \\
  --data-binary @event.json
# Missing / wrong / malformed signature, or a tampered body -> 403 (fail-closed).`,
          caption:
            "The signature is verified over the exact bytes received, not a re-serialised parse.",
        },
        {
          kind: "key-value",
          pairs: [
            { key: "Signature header", value: [{ code: "X-Courimax-Signature: sha256=<hex>" }] },
            {
              key: "Algorithm",
              value: ["HMAC-SHA256 over the raw request body, constant-time compared."],
            },
            {
              key: "Key",
              value: [
                { code: "COURIMAX_API_KEY" },
                " — the vendor confirmed the signing key IS the API key; there is no separate webhook secret.",
              ],
            },
          ],
        },
        {
          kind: "paragraph",
          text: "A valid signature proves Courimax sent the event — not that the order is theirs to overwrite. Four more layers sit behind it:",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { strong: "Object-level authorization" },
              " — the event must name the courier order id of an order we actually dispatched; otherwise it is refused (an ops signal, no write).",
            ],
            [
              { strong: "Idempotency" },
              " — status only advances monotonically (an atomic conditional update), so a redelivery or out-of-order retry cannot move state backwards.",
            ],
            [
              { strong: "Its own rate-limit bucket" },
              " (300 / minute) — the throttler runs BEFORE the signature guard so signature-guessing is counted, and the courier's five-attempt retry schedule can never inherit the tightest bucket and go dark.",
            ],
            [
              { strong: "A 64 KB pre-auth body cap" },
              " and a proof-of-delivery URL host allow-list (",
              { code: "storage.courimax.com" },
              ") — body parsers run before guards, so the ceiling is enforced before any work.",
            ],
          ],
        },
        {
          kind: "heading",
          id: "courimax-webhook-urls",
          text: "Webhook URLs per environment",
        },
        {
          kind: "table",
          headers: ["Env", "Webhook URL", "Reachability"],
          rows: [
            [
              { strong: "dev" },
              { code: "https://api-dev.yango.moveodevelop.com/api/dispatch/events" },
              "The dev ALB is office-IP-only (fail-closed) — Courimax's egress IPs must be allow-listed (or a path-scoped rule added) or its POSTs are blocked before the app.",
            ],
            [
              { strong: "staging" },
              { code: "https://api-staging.yango.moveodevelop.com/api/dispatch/events" },
              "The direct abstraction-API host (covered by the *.yango.moveodevelop.com wildcard cert). Same office-IP-only ALB as the other tiers — Courimax's egress IPs must be reachable to it for events to land.",
            ],
          ],
        },
        {
          kind: "heading",
          id: "courimax-status-vocab",
          text: "Status vocabulary",
        },
        {
          kind: "paragraph",
          text: [
            "The courier adapter translates the vendor's spellings into this closed set exactly once. Inbound events carry one of: ",
            { code: "pending" },
            ", ",
            { code: "assigned" },
            ", ",
            { code: "picked-up" },
            ", ",
            { code: "in-transit" },
            ", ",
            { code: "delivered" },
            ", ",
            { code: "failed" },
            ", ",
            { code: "cancelled" },
            ". (",
            { code: "not-dispatched" },
            " is the platform's own pre-dispatch sentinel — never an inbound status.)",
          ],
        },
        {
          kind: "heading",
          id: "courimax-failure-reasons",
          text: "Why an order didn't dispatch",
        },
        {
          kind: "paragraph",
          text: [
            "When the push fails the order still stands; the reason is recorded on the Sales Order (",
            { code: "custom_dispatch_failure_reason" },
            ") from this closed set:",
          ],
        },
        {
          kind: "table",
          headers: ["Reason", "Meaning"],
          rows: [
            [
              { code: "courier-unreachable" },
              "No HTTP response — DNS/connection/timeout, or an SSRF guard refused.",
            ],
            [
              { code: "courier-rejected" },
              "The courier answered non-2xx (includes the slot-full race).",
            ],
            [
              { code: "courier-answer-unusable" },
              "Accepted, but returned no usable order id — no join key for events.",
            ],
            [
              { code: "dispatch-not-configured" },
              "A required outbound value (pickup point/address/package size) is unset.",
            ],
            [
              { code: "booking-context-lost" },
              "The read and the write did not share one request scope.",
            ],
            [
              { code: "booking-key-incomplete" },
              "The availability answer lacked a field the create needs (slot template / dropoff).",
            ],
            [
              { code: "dispatch-disabled" },
              [{ code: "COURIER_PROVIDER=none" }, " — the noop's disabled envelope."],
            ],
          ],
        },
        {
          kind: "heading",
          id: "courimax-erpnext-side",
          text: "The ERPNext side + release ordering",
        },
        {
          kind: "paragraph",
          text: [
            "Accepted events are written to the read-only Courier Dispatch section on the submitted Sales Order via the whitelisted ",
            { code: "record_dispatch_state" },
            " method in ",
            { code: "yango-customizations" },
            " (status, courier order id, driver, proof-of-delivery) plus a Courier Dispatch Event audit row.",
          ],
        },
        {
          kind: "callout",
          tone: "danger",
          title: "Bake the customizations image FIRST",
          text: [
            "A REST write to a Custom Field that does not exist yet is silently dropped by Frappe with a 2xx. The ",
            { code: "yango-customizations" },
            " release carrying the dispatch fields must be baked into the ERPNext image and migrated BEFORE ",
            { code: "apps/api" },
            " is pointed at it — otherwise ",
            { code: "record_dispatch_state" },
            " detects the missing columns and returns ",
            { code: "unwritable" },
            " so apps/api can raise a ",
            { code: "dispatch-state-unwritable" },
            " ops signal instead of believing the write landed.",
          ],
        },
      ],
    },
    {
      slug: "internal-service-to-service",
      title: "Internal (service-to-service)",
      description: "How apps/api reaches the products on the private network.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Once ",
            { code: "apps/api" },
            " is deployed, it reaches the products over ECS Service Connect on the private network — ",
            { strong: "not" },
            " the public ALB. URLs resolve to internal service names:",
          ],
        },
        {
          kind: "table",
          headers: ["Target", "Internal URL"],
          rows: [
            [{ strong: "ERPNext" }, { code: "ERPNEXT_BASE_URL=http://frontend:8080" }],
            [{ strong: "UnoPIM" }, [{ code: "web.<env>-unopim.internal:80" }, " (Cloud Map)"]],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Secret boundary",
          text: "The webapp holds no product secret and never learns product URLs. Only apps/api knows the internal hosts and the credentials.",
        },
      ],
    },
  ],
};
